import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Text,
  Flex,
  useMediaQuery,
  useDisclosure,
  Input,
  Box,
} from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import FigmaEditIcon from 'components/Icons/FigmaEditIcon';
import FigmaMenuDotsIcon from 'components/Icons/FigmaMenuDotsIcon';
import FigmaMoveIcon from 'components/Icons/FigmaMoveIcon';
import FigmaAddPageIcon from 'components/Icons/FigmaAddPageIcon';
import FigmaPlusIcon from 'components/Icons/FigmaPlusIcon';
import FigmaSearchIcon from 'components/Icons/FigmaSearchIcon';
import FigmaTrashIcon from 'components/Icons/FigmaTrashIcon';
import FigmaTriangleDown from 'components/Icons/FigmaTriangleDown';
import { AppDispatch } from 'utils/GlobalHelpers';
import {
  accent,
  controlHeight,
  font,
  fontSize,
  neutral,
  primaryAction,
  radius,
  shadow,
  text as textToken,
} from 'styles/revampTokens';
import {
  prepareCreatePayload,
  getDashboard,
  setDashboard,
  setTimeFilter,
  getIsPageBarOpen,
  updatePageOrder,
  getSelectedPage,
  setSelectedPage,
  openPageModal,
  deletePage,
  reorderPages,
} from 'redux/DashboardSliceV2';

import DeleteModal from './components/DeleteModal';
import ExportButtonNew from './Export/ExportButtonNew';
import TimeRangeFilter from './TimeRangeFilter';
import SelectScenario from './SelectScenario';
import InstrumentMenuList from './InstrumentMenuList';

const Header = () => {
  const dispatch: AppDispatch = useDispatch();
  const isPageBarOpen = useSelector(getIsPageBarOpen);
  const [smallDevice] = useMediaQuery('(max-width: 1100px)');
  const isSmallDevice = (smallDevice && isPageBarOpen) || smallDevice;
  const dashboard = useSelector(getDashboard);
  const selectedPage = useSelector(getSelectedPage);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [pageSearch, setPageSearch] = useState('');
  const [pageToDelete, setPageToDelete] = useState<any>(null);
  const {
    isOpen: isDeleteModalOpen,
    onOpen: openDeleteModal,
    onClose: closeDeleteModal,
  } = useDisclosure();

  const pages = dashboard?.pages || [];
  const sortedPages = [...pages]
    .sort((a: any, b: any) => a.position - b.position)
    .filter((page: any) => (page.name || '').toLowerCase().includes(pageSearch.toLowerCase()));

  const setSelectedPageAndPersist = (page: any) => {
    dispatch(setSelectedPage(page));
    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    data[dashboard.model_id] = {
      ...data[dashboard.model_id],
      selectedPageId: page?.id,
    };
    localStorage.setItem('global-modal-filters', JSON.stringify(data));
  };

  const handleCreatePage = () => {
    dispatch(openPageModal({}));
  };

  const handleEditPage = (page: any) => {
    dispatch(openPageModal(page));
  };

  const handleDeletePage = (page: any) => {
    setPageToDelete(page);
    openDeleteModal();
  };

  const confirmDeletePage = () => {
    if (pageToDelete) {
      dispatch(deletePage({ pageId: pageToDelete.id }));
      setPageToDelete(null);
      closeDeleteModal();
    }
  };

  const cancelDeletePage = () => {
    setPageToDelete(null);
    closeDeleteModal();
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    const displayedPages = [...sortedPages].sort((a: any, b: any) => a.position - b.position);
    const reorderedPages = Array.from(displayedPages);
    const [movedPage] = reorderedPages.splice(sourceIndex, 1);
    reorderedPages.splice(destinationIndex, 0, movedPage);

    const updatedPages = reorderedPages.map((page, index) => ({
      ...(page as any),
      position: index,
    }));

    const nonFilteredPages = pages.filter(
      (page: any) => !sortedPages.some((sp: any) => sp.id === page.id),
    );
    const allUpdatedPages = [...nonFilteredPages, ...updatedPages];

    dispatch(reorderPages(allUpdatedPages));

    try {
      const updatedDashboard = {
        ...dashboard,
        pages: allUpdatedPages,
      };
      await dispatch(updatePageOrder({ dashboard: updatedDashboard }));
    } catch {
      dispatch(reorderPages(pages));
    }
  };

  const handleNameDoubleClick = () => {
    setEditedName(dashboard?.name || '');
    setIsEditingName(true);
  };

  const handleNameSave = async () => {
    if (dashboard && editedName.trim()) {
      try {
        const updatedDashboard = {
          ...dashboard,
          name: editedName.trim(),
        };
        await dispatch(updatePageOrder({ dashboard: updatedDashboard })).then(() => {
          dispatch(setDashboard(updatedDashboard));
        });
      } catch {
        // Handle error if needed
      }
    }
    setIsEditingName(false);
  };

  const handleNameKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleNameSave();
    } else if (event.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  const onSelectInstrument = (widgetType: string) => dispatch(prepareCreatePayload(widgetType));
  const rangeSelected = (start: string, end: string) => {
    if (dashboard?.id) {
      dispatch(setTimeFilter({ start, end }));
    }
  };

  return (
    // Same rhythm as blocksv2's BlockHeader: mx='-3'/px='3' (12px) horizontal
    // inset, 12px vertical padding. index.tsx's outer wrapper was moved to
    // px={3} to match, so this and the content grid below it stay in step.
    <Box
      bg={neutral.surface}
      borderBottom={`1px solid ${neutral.border}`}
      mx='-3'
      px='3'
      py='12px'
      display='flex'
      flexDirection='column'
      gap='16px'
    >
      <Flex maxW={'100vw'} justifyContent={'space-between'} alignItems='center' gap='12px'>
        <Flex alignItems='center' gap='16px'>
          {isEditingName ? (
            <Input
              value={editedName}
              onChange={(event) => setEditedName(event.target.value)}
              onBlur={handleNameSave}
              onKeyDown={handleNameKeyPress}
              color={textToken.primary}
              fontSize={fontSize.title}
              fontWeight='600'
              fontFamily={font.display}
              bg={neutral.surface}
              border={`1px solid ${neutral.border}`}
              _focus={{
                borderColor: accent.solid,
                boxShadow: 'none',
              }}
              maxWidth='450px'
              width='fit-content'
              autoFocus
              height='auto'
              minHeight='36px'
            />
          ) : (
            <Text
              alignContent='center'
              color={textToken.primary}
              fontSize={fontSize.title}
              whiteSpace='nowrap'
              overflow='hidden'
              fontWeight='600'
              fontFamily={font.display}
              maxWidth='450px'
              textOverflow='ellipsis'
              cursor='pointer'
              onDoubleClick={handleNameDoubleClick}
              _hover={{ opacity: 0.8 }}
              title='Double-click to edit dashboard name'
            >
              {dashboard?.name}
            </Text>
          )}
          {/* Same wrapper as DimensionsRevamp/Header.tsx's Baseline+date pill
              — bg/border/radius/height already matched; gap was the one
              value still off (14px here vs 12px there). */}
          <Box
            display='flex'
            alignItems='center'
            gap='12px'
            bg={neutral.surface}
            border={`1px solid ${neutral.border}`}
            borderRadius={radius.control}
            overflow='hidden'
            height={controlHeight}
          >
            <SelectScenario />
            {dashboard && <TimeRangeFilter rangeSelected={rangeSelected} />}
          </Box>
        </Flex>
        <Flex gap='16px' alignItems='center'>
          {/* Same shape as blocksv2's HeaderControls search field — bordered
              neutral pill, icon and placeholder in the muted token, accent
              reserved for focus rather than sitting on the idle icon. */}
          <Box
            display={{ base: 'none', xl: 'flex' }}
            alignItems='center'
            gap='7px'
            bg={neutral.surface}
            border={`1px solid ${neutral.border}`}
            borderRadius={radius.control}
            height={controlHeight}
            px='12px'
            width='217px'
            transition='border-color 0.12s ease'
            _hover={{ borderColor: neutral.borderStrong }}
            _focusWithin={{ borderColor: neutral.borderStrong }}
          >
            <FigmaSearchIcon width={14} height={14} color={textToken.muted} />
            <Input
              variant='unstyled'
              placeholder='Search dashboard page'
              value={pageSearch}
              onChange={(event) => setPageSearch(event.target.value)}
              fontSize={fontSize.body}
              fontWeight='500'
              fontFamily={font.body}
              color={textToken.primary}
              _placeholder={{ color: textToken.muted }}
            />
          </Box>
          {/* <ExportButton /> */}
          <ExportButtonNew />
          {/* Prop-for-prop the same trigger as blocksv2/HeaderControls's
              "Create block" and DimensionsRevamp's "Create" — primaryAction
              (near-black) rather than the purple accent, same control height
              and corner. */}
          <Menu variant={'dashboarding'}>
            {({ isOpen }) => {
              return (
                <>
                  <MenuButton
                    backgroundColor={primaryAction.bg}
                    padding='0 14px'
                    borderRadius={radius.control}
                    as={Button}
                    title={'Create'}
                    border={'none'}
                    _hover={{ backgroundColor: primaryAction.hover }}
                    _active={{ backgroundColor: primaryAction.hover }}
                    rightIcon={<FigmaTriangleDown width={9} height={5} color={textToken.inverse} />}
                    leftIcon={<FigmaPlusIcon width={13} height={13} color={textToken.inverse} />}
                    fontSize={fontSize.body}
                    fontWeight={'500'}
                    fontFamily={font.body}
                    color={textToken.inverse}
                    width={'fit-content'}
                    height={controlHeight}
                  >
                    {isSmallDevice ? '' : 'Create'}
                  </MenuButton>
                  {isOpen && (
                    <MenuList
                      zIndex={18}
                      p={0}
                      minW='153px'
                      bg={neutral.surface}
                      border={`1px solid ${neutral.border}`}
                      borderRadius={radius.overlay}
                      overflow='hidden'
                      boxShadow={shadow.overlay}
                    >
                      <InstrumentMenuList onSelectInstrument={onSelectInstrument} />
                    </MenuList>
                  )}
                </>
              );
            }}
          </Menu>
        </Flex>
      </Flex>

      {/* Same geometry as blocksv2's category strip (categoryStripTokens.ts):
          32px pill height, 12px inset, 10px corner, 13px label, tabs flush
          with no gap, accent only on the selected pill. No "PAGES" caption —
          the strip reads as navigation on its own, the same way the category
          strip needs no label above it. */}
      {pages.length > 0 && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId='pages-strip' direction='horizontal'>
            {(provided) => (
              <Flex
                align='center'
                wrap='wrap'
                gap='0px'
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {sortedPages.map((page: any, index: number) => {
                  const isSelected = selectedPage?.id === page.id;
                  return (
                    <Draggable key={page.id} draggableId={String(page.id)} index={index}>
                      {(dragProvided) => (
                        <Flex
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          as='button'
                          role='group'
                          align='center'
                          height={controlHeight}
                          gap='11px'
                          onClick={() => setSelectedPageAndPersist(page)}
                          px='12px'
                          borderRadius='10px'
                          bg={isSelected ? accent.subtle : neutral.surface}
                          color={isSelected ? accent.solid : '#121826'}
                          fontFamily={font.body}
                          fontSize='13px'
                          fontWeight={isSelected ? '600' : '400'}
                          whiteSpace='nowrap'
                          _hover={{ bg: isSelected ? accent.subtle : neutral.surfaceHover }}
                          _active={{ transform: 'scale(0.97)' }}
                          transition='background-color 0.15s, transform 0.1s ease'
                        >
                          {page.name || `Page ${page.id}`}
                          {isSelected && (
                            <Box
                              {...dragProvided.dragHandleProps}
                              cursor='grab'
                              display='flex'
                              onClick={(event) => event.stopPropagation()}
                            >
                              <FigmaMoveIcon width={14} height={14} color={accent.solid} />
                            </Box>
                          )}
                          <Menu placement='bottom-end'>
                            {({ isOpen: isPageMenuOpen }) => {
                              // `isPageMenuOpen` is what stops the flicker:
                              // while the menu is open the pointer sits over
                              // the MenuList, not the pill, so `_groupHover`
                              // drops and this trigger would animate back to
                              // width 0. Chakra re-anchors the popper to the
                              // shrinking trigger, which slides the menu off
                              // the pointer, hover returns, and the whole
                              // thing oscillates. Pinning the trigger open
                              // keeps the anchor a fixed size.
                              const showTrigger = isSelected || isPageMenuOpen;
                              return (
                                <>
                                  <MenuButton
                                    as={Box}
                                    display='flex'
                                    alignItems='center'
                                    overflow='hidden'
                                    width={showTrigger ? '14px' : '0px'}
                                    marginLeft={showTrigger ? '2px' : '-2px'}
                                    opacity={showTrigger ? 1 : 0}
                                    pointerEvents={showTrigger ? 'auto' : 'none'}
                                    transition='width 0.15s ease, margin-left 0.15s ease, opacity 0.15s ease'
                                    _groupHover={{
                                      width: '14px',
                                      marginLeft: '2px',
                                      opacity: 1,
                                      pointerEvents: 'auto',
                                    }}
                                    cursor='pointer'
                                    onClick={(event: any) => event.stopPropagation()}
                                  >
                                    {/* secondary (not muted) is dark enough to
                                        register as a real affordance on the
                                        resting pill without going oversized. */}
                                    <FigmaMenuDotsIcon
                                      width={14}
                                      height={14}
                                      color={
                                        isSelected || isPageMenuOpen
                                          ? accent.solid
                                          : textToken.secondary
                                      }
                                    />
                                  </MenuButton>
                                  <MenuList
                                    zIndex={18}
                                    p={0}
                                    minWidth='153px'
                                    bg={neutral.surface}
                                    border={`1px solid ${neutral.border}`}
                                    borderRadius={radius.overlay}
                                    overflow='hidden'
                                    boxShadow={shadow.overlay}
                                  >
                                    <MenuItem
                                      bg={neutral.surface}
                                      gap='8px'
                                      p='9px 11px'
                                      borderRadius={0}
                                      fontFamily={font.body}
                                      fontSize='13px'
                                      color={textToken.primary}
                                      _hover={{ bg: neutral.surfaceHover }}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleEditPage(page);
                                      }}
                                    >
                                      <FigmaEditIcon
                                        width={14}
                                        height={14}
                                        color={textToken.primary}
                                      />
                                      Edit
                                    </MenuItem>
                                    {pages.length > 1 && (
                                      <MenuItem
                                        bg={neutral.surface}
                                        gap='8px'
                                        p='9px 11px'
                                        borderRadius={0}
                                        fontFamily={font.body}
                                        fontSize='13px'
                                        color={textToken.primary}
                                        _hover={{ bg: neutral.surfaceHover }}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleDeletePage(page);
                                        }}
                                      >
                                        <FigmaTrashIcon
                                          width={14}
                                          height={14}
                                          color={textToken.primary}
                                        />
                                        Delete
                                      </MenuItem>
                                    )}
                                  </MenuList>
                                </>
                              );
                            }}
                          </Menu>
                        </Flex>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
                <Box
                  as='button'
                  onClick={handleCreatePage}
                  display='flex'
                  alignItems='center'
                  height={controlHeight}
                  gap='8px'
                  px='12px'
                  borderRadius='8px'
                  color={accent.solid}
                  fontFamily={font.body}
                  fontSize='13px'
                  whiteSpace='nowrap'
                  transition='transform 0.1s ease'
                  _active={{ transform: 'scale(0.94)' }}
                >
                  <FigmaAddPageIcon width={16} height={16} />
                  Add new Page
                </Box>
              </Flex>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={cancelDeletePage}
        itemName={pageToDelete?.name || `Page ${pageToDelete?.id}`}
        onDelete={confirmDeletePage}
      />
    </Box>
  );
};

export default Header;
